import { ChevronsUpDown, Info, LogOut } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/Sidebar';
import config from '@/config';
import { getKeycloakRoles } from '@/features/Keycloak/keycloakService';
import { SystemInfoDialog } from '@/features/System/SystemDialog';
import { useKeycloak } from '@/hooks/useKeycloak';
import { BuildVersion, getWebappBuildVersion } from '@/utils/meta.function';
import { DropdownMenuGroup } from '@radix-ui/react-dropdown-menu';
import { useEffect, useState } from 'react';
import { Avatar, AvatarFallback } from './ui/Avatar';
import { Badge } from './ui/Badge';

export function NavUser({
  user,
}: {
  user: {
    name: string | undefined;
    userName: string | undefined;
  };
}) {
  const { isMobile, open } = useSidebar();
  const { logout } = useKeycloak();
  const [versionDialogOpen, setVersionDialogOpen] = useState(false);
  const [serverVersion, setServerVersion] = useState<BuildVersion>();
  const [clientVersion, _setClientVersion] = useState<BuildVersion>(
    getWebappBuildVersion(),
  );

  useEffect(() => {
    const fetchServerVersion = async () => {
      const getVersionUrl = config.rest.url.replace('/api', '/info/build');
      try {
        const response = await fetch(getVersionUrl);
        if (!response.ok) throw new Error('Server build version fetch failed');
        const jsonObject = await response.json();
        const version: BuildVersion = {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          tag: jsonObject._build_Tag,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          branch: jsonObject.build_branch,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          hash: jsonObject.build_hash,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
          time: jsonObject.build_time,
        };
        setServerVersion(version);
        console.log('Server BuildVersion: ', jsonObject);
      } catch (error) {
        console.error('Error fetching server build version', error);
      }
    };

    void fetchServerVersion();
  }, []);

  useEffect(() => {
    console.log('Client BuildVersion: ', clientVersion);
  }, [clientVersion]);

  async function logoutUser() {
    console.log('logout');
    await logout();
  }

  return (
    <>
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size="lg"
                className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
              >
                <Avatar
                  className={`hidden h-8 w-8 rounded-lg ${open ? '' : 'block'}`}
                >
                  {/* <AvatarImage src={user.avatar} alt={user.name} /> */}
                  {/* <User className="rounded-lg" /> */}
                  <AvatarFallback className="rounded-lg">
                    {user.name?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user.name}</span>
                  <div className="flex gap-2">
                    {(getKeycloakRoles() ?? [])?.map((role) => {
                      return (
                        <Badge
                          variant={'secondary'}
                          className="text-xs capitalize"
                          key={role}
                        >
                          {role}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
                <ChevronsUpDown className="ml-auto size-4" />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
              side={isMobile ? 'bottom' : 'right'}
              align="end"
              sideOffset={4}
            >
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                  {/* <Avatar className="h-8 w-8 rounded-lg">
                    <AvatarImage src={user.avatar} alt={user.name} />
                    <AvatarFallback className="rounded-lg">
                      {user.userName?.charAt(0).toUpperCase()}
                    </AvatarFallback>
                    <AvatarFallback className="rounded-lg">CN</AvatarFallback>
                  </Avatar> */}
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-medium">{user.name}</span>
                    <div className="flex gap-2">
                      {(getKeycloakRoles() ?? [])?.map((role) => {
                        return (
                          <Badge
                            variant={'secondary'}
                            className="text-xs capitalize"
                            key={role}
                          >
                            {role}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </DropdownMenuLabel>
              {/* <DropdownMenuSeparator /> */}
              <DropdownMenuGroup>
                <DropdownMenuItem
                  onClick={() => {
                    setVersionDialogOpen(true);
                  }}
                >
                  <Info />
                  System Info
                </DropdownMenuItem>
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => void logoutUser()}>
                <LogOut />
                Log out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      {versionDialogOpen && (
        <SystemInfoDialog
          open={versionDialogOpen}
          clientVersion={clientVersion}
          serverVersion={serverVersion}
          onToggleOpen={() => {
            setVersionDialogOpen(!versionDialogOpen);
          }}
        />
      )}
    </>
  );
}
